import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Progress from './Progress';
import Details from '../Details/Details';
import ButtonSpinner from '../../button/ButtonSpinner';
import ConfirmModalDialog from '../../confirm/ConfirmModalDialog';
import routes from '@configs/routes.json';
import { store } from '@store';

import './Upload.scss';

import { IUpload, UploadStatus } from '@models/upload';

export interface IUploadStateProps extends IUpload {}

export interface IUploadDispatchProps {
  cancel: () => void;
  remove: () => void;
}

const Upload: React.FC<IUploadStateProps & IUploadDispatchProps> = ({
  id,
  status,
  uploadProgress,
  uploadStartDate,
  uploadedBy,
  cancel,
  experimentListName,
  message,
  experiment,
  path,
  transmitStarted,
  transmitEnded,
  remove,
  isInFinalStage,
}) => {
  const { t } = useTranslation();
  const [showConfirmDialog, setShowConfirmModalDialog] = React.useState(false);
  const transmitStartTime = transmitStarted
    ? new Date(transmitStarted).getTime()
    : Date.now();

  const transmitEndTime = transmitEnded
    ? new Date(transmitEnded).getTime()
    : Date.now();
  const transmitElapsed = transmitEndTime - transmitStartTime;
  const hoursElapsed = Math.floor(transmitElapsed / 3600000)
    .toString()
    .padStart(2, '0');
  const minutesElapsed = (Math.floor(transmitElapsed / 60000) % 60)
    .toString()
    .padStart(2, '0');

  const onCancel = () => {
    if (status & (UploadStatus.uploadPending | UploadStatus.uploading)) {
      setShowConfirmModalDialog(true);
      return;
    }
    cancel();
  };

  const onConfirmDialogConfirm = () => {
    setShowConfirmModalDialog(false);
  };

  const onConfirmDialogCancel = () => {
    setShowConfirmModalDialog(false);
    cancel();
  };

  const uploadIcon = () => {
    switch (status) {
      case UploadStatus.uploadPending:
      case UploadStatus.uploading:
      case UploadStatus.uploadClaimed:
        return (
          <div
            className='spinner-border fcolor--upload-passed-status mt-1'
            role='status'
            key='uploading-icon'
          >
            <span className='sr-only'>Loading...</span>
          </div>
        );
      case UploadStatus.uploadInterrupted:
        return (
          <div
            className='spinner-border fcolor--upload-warning mt-1'
            role='status'
            key='upload-interrupted-icon'
          >
            <span className='sr-only'>Loading...</span>
          </div>
        );
      case UploadStatus.uploadFailed:
        return (
          <div className='icon-size upload-icon' key='upload-failed-icon'>
            <i className='fas fa-times-circle fcolor--error'></i>
          </div>
        );
      case UploadStatus.uploadCancelled:
      case UploadStatus.uploadCancelling:
        return (
          <div className='icon-size upload-icon' key='upload-cancelled-icon'>
            <i className='fas fa-ban fcolor--text-label'></i>
          </div>
        );
      case UploadStatus.uploaded:
        return (
          <div className='icon-size upload-icon' key='uploaded-icon'>
            <i className='fas fa-check-circle fcolor--upload-passed-status'></i>
          </div>
        );
    }
  };

  const messageElement = !!message && (
    <span className='ml-2 fcolor--data-values'>{message}</span>
  );

  const uploadStatusInformation = () => {
    switch (status) {
      case UploadStatus.uploadPending:
        return (
          <div className='fcolor--upload-status'>
            <span className='font-weight-bold'>
              {t('upload_uploadPending')}
            </span>
          </div>
        );
      case UploadStatus.uploadClaimed:
        return (
          <div className='d-flex'>
            <div className='font-weight-bold fcolor--upload-status'>
              {t('upload_claimed')}
            </div>
            <div className='ml-2 uploading-progress-bar'>
              <Progress percent={0} />
            </div>
            <div className='ml-2 fcolor--text-label'>
              {t('upload_elapsedTimeEmpty')}
            </div>
          </div>
        );
      case UploadStatus.uploading:
        return (
          <div className='d-flex'>
            <div className='font-weight-bold fcolor--upload-status'>
              {t('upload_uploading')}
            </div>
            <div className='ml-2 uploading-progress-bar'>
              <Progress percent={uploadProgress} />
            </div>
            <div className='ml-2 fcolor--text-label font-weight-semi-bold'>
              {t('upload_elapsedTime', {
                hours: hoursElapsed,
                minutes: minutesElapsed,
              })}
            </div>
          </div>
        );
      case UploadStatus.uploadInterrupted:
        return (
          <React.Fragment>
            <span className='fcolor--upload-warning font-weight-bold'>
              {t('upload_uploadInterrupted')}
            </span>
            {messageElement}
          </React.Fragment>
        );
      case UploadStatus.uploadFailed:
        return (
          <React.Fragment>
            <span className='fcolor--error font-weight-bold'>
              {t('upload_uploadFailed')}
            </span>
            {messageElement}
          </React.Fragment>
        );
      case UploadStatus.uploadCancelled:
      case UploadStatus.uploadCancelling:
        return (
          <React.Fragment>
            <span className='fcolor--text-label font-weight-bold'>
              {t('upload_uploadCancelled')}
            </span>
            {messageElement}
          </React.Fragment>
        );
      case UploadStatus.uploaded:
        return (
          <div className='d-flex'>
            <div className='font-weight-bold fcolor--upload-status'>
              {t('upload_complete')}
            </div>
            <div className='ml-2 uploading-progress-bar'>
              <Progress percent={100} />
            </div>
            <div className='ml-2 fcolor--text-label font-weight-semi-bold'>
              {t('upload_elapsedTime', {
                hours: hoursElapsed,
                minutes: minutesElapsed,
              })}
            </div>
          </div>
        );
    }
  };

  const icon = uploadIcon();
  const statusInformation = uploadStatusInformation();

  return (
    <div className='active-upload'>
      <div className='py-2'>
        <div className='float-right'>
          {status &
          (UploadStatus.uploadFailed |
            UploadStatus.uploadCancelled |
            UploadStatus.uploaded |
            UploadStatus.uploadRemoving) ? (
            <ButtonSpinner
              buttonClass='btn-white'
              isDisabled={!isInFinalStage}
              isSpinning={status === UploadStatus.uploadRemoving}
              onClick={remove}
            >
              {t('upload_removeFromList', { uppercase: true })}
            </ButtonSpinner>
          ) : (
            <ButtonSpinner
              buttonClass='btn-light'
              isDisabled={false}
              isSpinning={status === UploadStatus.uploadCancelling}
              onClick={onCancel}
            >
              {t('upload_cancelUpload', { uppercase: true })}
            </ButtonSpinner>
          )}
        </div>
        <div className='d-flex'>
          <div className='mr-2 icon-wrapper'>{icon}</div>
          <div>
            <div>
              {status === UploadStatus.uploaded ? (
                <span className='font-weight-bold experiment-list-name'>
                  <a
                    className='download-link'
                    href={`${routes.app.expListFileDownload.replace(
                      ':id',
                      id
                    )}?projectid=${encodeURIComponent(
                      store.getState().projectPicker.activeProjectId as string
                    )}`}
                    download
                  >
                    {experimentListName}
                  </a>
                </span>
              ) : (
                <span className='font-weight-bold experiment-list-name'>
                  {experimentListName}
                </span>
              )}
            </div>
            <div className='font-size-13 fcolor--text-label clearfix'>
              <div className='float-left'>
                {t('upload_signature', {
                  date: uploadStartDate,
                  user: uploadedBy,
                })}
              </div>
              {!!path && (
                <React.Fragment>
                  <div className='float-left ml-2'>Run Folder:</div>
                  <div dir='rtl' className='run-folder-path' title={path}>
                    {path}
                  </div>
                </React.Fragment>
              )}
            </div>
            <div className='font-size-13 fcolor--text-label clearfix'>
              <div className='float-left'>ID:</div>
              <div className='float-left'>{id}</div>
            </div>
          </div>
        </div>
      </div>
      <hr className='m-0' />
      <div className='py-2'>{statusInformation}</div>
      <div>
        {!!experiment && (
          <Details experiment={experiment} isCollapsible={true} />
        )}
      </div>
      <ConfirmModalDialog
        show={showConfirmDialog}
        title={t('upload_cancelUpload')}
        confirm={() => onConfirmDialogConfirm()}
        cancel={() => onConfirmDialogCancel()}
        confirmLabel={t('upload_keepUploading', { uppercase: true })}
        cancelLabel={t('upload_cancelUpload', { uppercase: true })}
        dismiss={onConfirmDialogConfirm}
        confirmButtonClass='btn-primary'
      >
        <div>{t('upload_cancelUpload_body')}</div>
        <div className='font-weight-bold'>
          {t('upload_cancelUpload_experimentList', {
            name: experimentListName,
          })}
        </div>
      </ConfirmModalDialog>
    </div>
  );
};

export default Upload;
